import { createApi } from '@reduxjs/toolkit/query/react';
import api from '../../api/config';

/**
 * Axios-backed baseQuery — keeps existing interceptors (Bearer token, 401, network toasts).
 */
/**
 * For FormData uploads, omit Content-Type so Axios/browser can set
 * multipart/form-data with the correct boundary.
 */
export const withMultipart = (headers = {}) => {
  if (headers && Object.keys(headers).length > 0) {
    const next = { ...headers };
    delete next['Content-Type'];
    delete next['content-type'];
    return next;
  }
  return {};
};

/**
 * PHP does not populate $_POST/$_FILES for multipart bodies on PUT/PATCH, so those
 * requests reach Laravel with an empty payload. Send them as POST and let Laravel's
 * method spoofing (`_method`) route them to the update action.
 */
const spoofFormDataMethod = (method, data, isFormData) => {
  if (!isFormData || !/^(PUT|PATCH)$/i.test(String(method))) return method;
  if (!data.has('_method')) data.append('_method', String(method).toUpperCase());
  return 'POST';
};

export const axiosBaseQuery =
  () =>
  async ({ url, method = 'GET', data, params, headers, responseType }) => {
    try {
      const isFormData =
        typeof FormData !== 'undefined' && data instanceof FormData;
      const result = await api({
        url,
        method: spoofFormDataMethod(method, data, isFormData),
        data,
        params,
        headers: isFormData ? withMultipart(headers) : headers,
        responseType,
      });
      return {
        data: result.data,
        meta: { headers: result.headers, status: result.status },
      };
    } catch (axiosError) {
      return {
        error: {
          status: axiosError.response?.status,
          data: axiosError.response?.data ?? axiosError.apiMessage ?? axiosError.message,
        },
      };
    }
  };

export const TAG_TYPES = [
  'Auth',
  'Lead',
  'Merchant',
  'Equipment',
  'Issue',
  'User',
  'Customer',
  'Role',
  'Permission',
  'Notification',
  'Team',
  'Meeting',
  'Document',
  'Lookup',
  'Dashboard',
  'ActivityLog',
  'Prospect',
  'Order',
  'Invoice',
  'Payment',
  'PaymentAccount',
  'PaymentError',
  'Brand',
  'Service',
  'Company',
  'Task',
  'TaskType',
  'TaskCategory',
  'TaskTemplate',
  'TaskComment',
  'CallRecord',
  'DailySummary',
  'Contract',
  'LeadQuiz',
  'QuickNote',
  'Status',
  'Report',
];

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery(),
  tagTypes: TAG_TYPES,
  endpoints: () => ({}),
});

/** List-style providesTags helper */
export const listTags = (type, result, idKey = 'id') => {
  const list =
    result?.data?.list ??
    result?.list ??
    (Array.isArray(result?.data) ? result.data : null) ??
    (Array.isArray(result) ? result : null);

  if (Array.isArray(list)) {
    return [
      ...list.map((item) => ({ type, id: item?.[idKey] ?? item })),
      { type, id: 'LIST' },
    ];
  }
  return [{ type, id: 'LIST' }];
};
